import React, {useEffect} from 'react';
import {Text} from '@gravity-ui/uikit';
import {
  ActionModal,
  EntityImageUploadButton,
  ErrorModal,
  FormInput,
  useErrorHandler,
  usePublicImageUpload,
} from '../../../../shared';
import {SelectCategoryGroups} from '../../../../entities';
import {useAddCategoryForm} from '../../model';
import styles from './AddCategoryModal.module.scss';

interface AddCategoryModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  categoryGroupId?: string | null;
  hideGroupSelect?: boolean;
}

export const AddCategoryModal = ({
  open,
  setOpen,
  categoryGroupId,
  hideGroupSelect,
}: AddCategoryModalProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isLoading,
    submitError,
    resetForm,
    submitForm,
  } = useAddCategoryForm({setOpen, initialGroupId: categoryGroupId ?? null});

  const {uploadId, isUploading, uploadImage, resetUpload} = usePublicImageUpload({
    resourceName: 'Изображение категории',
    notificationKey: 'category-image-upload-error',
  });

  const isGroupSelectHidden = hideGroupSelect ?? Boolean(categoryGroupId);

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Категория',
    title: 'Ошибка при добавлении категории',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setFieldValue('imageUploadId', uploadId || null);
  }, [uploadId, setFieldValue]);

  const handleGroupChange = (value: string[]) => {
    setFieldValue('categoryGroupId', value[0] || null);
    setFieldTouched('categoryGroupId', true, false);
  };

  const handleFileSelect = (file: File) => {
    uploadImage(file);
  };

  const handleRemoveImage = () => {
    resetUpload();
    setFieldValue('imageUploadId', null);
  };

  const selectedGroupValue = values.categoryGroupId ? [values.categoryGroupId] : [];

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Добавить категорию'}
        textButtonApply={'Сохранить'}
        textButtonCancel={'Отменить'}
        isDisabled={isUploading}>
        <FormInput
          label="Название"
          placeholder="Название категории"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && errors.name}
        />
        <FormInput
          label="Порядок отображения"
          placeholder="Порядок отображения"
          name="order"
          value={values.order.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.order && errors.order}
        />
        {!isGroupSelectHidden && (
          <div className={styles.fieldContainer}>
            <Text variant="subheader-1" className={styles.label}>
              Группа категорий
            </Text>
            <SelectCategoryGroups
              value={selectedGroupValue}
              onUpdate={handleGroupChange}
              name="categoryGroupId"
              size="l"
              placeholder="Выберите группу"
              error={touched.categoryGroupId && errors.categoryGroupId}
            />
          </div>
        )}
        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Изображение
          </Text>
          <EntityImageUploadButton
            isUploading={isUploading}
            onFileSelect={handleFileSelect}
            onRemove={handleRemoveImage}
            notificationKey="category-image-upload-error"
          />
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
